import React from "react";
import {
  Typography,
  Box,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@mui/material";
import produce from "immer";
import { initialState } from "./initialState";

export const lensPrescriptionHeaders = ["Lens Prescription", "SPH", "CYL", "Axis", "ADD"];

interface LensPrescriptionProps {
  visionData: typeof initialState;
  onVisionChange: (updatedData: typeof initialState) => void;
}

const COLS = [
  { key: "sph", label: "SPH" },
  { key: "cyl", label: "CYL" },
  { key: "axis", label: "Axis" },
  { key: "add", label: "ADD" }
] as const;

const ROWS = ["OD", "OS"] as const;

export default function LensPrescription({
  visionData,
  onVisionChange
}: LensPrescriptionProps): React.JSX.Element {
  const handleChange = (
    row: (typeof ROWS)[number],
    col: (typeof COLS)[number]["key"],
    value: string
  ) => {
    const updated = produce(visionData, (draft: typeof initialState) => {
      if (!draft.lensPrescription) {
        draft.lensPrescription = JSON.parse(JSON.stringify(initialState.lensPrescription));
      }
      if (!draft.lensPrescription[row]) {
        draft.lensPrescription[row] = JSON.parse(
          JSON.stringify(initialState.lensPrescription[row] || {})
        );
      }
      const eyeData = draft.lensPrescription[row] as unknown as Record<string, string | null>;
      if (eyeData) {
        eyeData[col] = value === "" ? null : value;
      }
    });
    onVisionChange(updated);
  };

  const handleRemarksChange = (value: string) => {
    const updated = produce(visionData, (draft: typeof initialState) => {
      if (!draft.remarks) {
        draft.remarks = { glassPrescription: "", lensPrescription: "" };
      }
      draft.remarks.lensPrescription = value;
    });
    onVisionChange(updated);
  };

  const currentLensPrescription = visionData?.lensPrescription;

  const cellStyle = {
    border: 1,
    borderColor: "divider"
  };

  const headerCellStyle = {
    ...cellStyle,
    fontWeight: 600,
    textAlign: "center" as const
  };

  return (
    <Box>
      <TableContainer>
        <Typography
          sx={{
            padding: 1,
            textAlign: "center",
            backgroundColor: "#ededed",
            borderLeft: 1,
            borderRight: 1,
            borderTop: 1,
            borderColor: "divider"
          }}
        >
          Lens Prescription
        </Typography>

        <Table size="small" sx={{ border: 1, borderColor: "divider", borderRadius: "5px" }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headerCellStyle, width: "150px" }} />
              {COLS.map((col) => (
                <TableCell key={col.key} sx={headerCellStyle}>
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {ROWS.map((row) => (
              <TableRow key={row}>
                <TableCell sx={{ ...headerCellStyle, textAlign: "left" }}>{row}</TableCell>
                {COLS.map((col) => (
                  <TableCell key={col.key} sx={cellStyle}>
                    <TextField
                      size="small"
                      variant="outlined"
                      fullWidth
                      value={currentLensPrescription?.[row]?.[col.key] ?? ""}
                      onChange={(e) => handleChange(row, col.key, e.target.value)}
                      sx={{
                        "& .MuiInputBase-input": {
                          padding: "4px 8px",
                          fontSize: "0.875rem"
                        }
                      }}
                    />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TextField
        sx={{ mt: 2 }}
        fullWidth
        variant="outlined"
        size="small"
        placeholder="Remarks"
        value={visionData?.remarks?.lensPrescription || ""}
        onChange={(e) => handleRemarksChange(e.target.value)}
      />
    </Box>
  );
}
